import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { sportColor, sportLabel, timeAgo, fmtDate, FLAG, PLACEHOLDER, onImgError } from "@/lib/utils";
import { useT } from "@/lib/i18n";
import { AddToCalendar } from "@/components/Countdown";
export { VideoCard } from "@/components/VideoCard";

export const SectionHead = ({ title, to, linkText = "View All", id }) => {
  const { t } = useT();
  return (
    <div className="mb-8 flex items-end justify-between" data-testid={`section-${id}`}>
      <h2 className="section-title">{t(title)}</h2>
      {to && <Link to={to} className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#047857]" data-testid={`section-${id}-link`}>{t(linkText)} <ArrowRight size={16} /></Link>}
    </div>
  );
};

export const SportBadge = ({ sport, className = "" }) => {
  const { t } = useT();
  return <span className={`text-[11px] font-extrabold uppercase tracking-wider ${className}`} style={{ color: sportColor(sport) }}>{t(sportLabel(sport))}</span>;
};

export const NewsCard = ({ item, big = false }) => (
  <Link to={`/news/${item.slug}`} data-testid={`news-card-${item.slug}`} className="group card-hover block overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm hover:shadow-xl" style={{ borderTop: `4px solid ${sportColor(item.sport)}` }}>
    <div className={`img-zoom relative overflow-hidden ${big ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
      <img src={item.image || PLACEHOLDER} onError={onImgError} alt="" loading="lazy" className="h-full w-full object-cover" />
      <span className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow" style={{ background: sportColor(item.sport) }}>{item.sport}</span>
    </div>
    <div className="p-4">
      <div className="mb-2 flex items-center justify-between"><SportBadge sport={item.sport} /><span className="text-xs text-slate-400">{timeAgo(item.published_at)}</span></div>
      <h3 className={`font-bold leading-snug text-slate-900 group-hover:text-[#047857] ${big ? "text-2xl" : "text-[17px] line-clamp-2"}`}>{item.title}</h3>
      <p className="mt-2 text-sm text-slate-500 line-clamp-2">{item.summary}</p>
      <p className="mt-2 text-[11px] text-slate-400">Source: {item.source}</p>
    </div>
  </Link>
);

export const NewsRow = ({ item }) => (
  <Link to={`/news/${item.slug}`} data-testid={`news-row-${item.slug}`} className="group flex gap-3 border-b border-slate-100 py-3 last:border-0">
    <img src={item.image || PLACEHOLDER} onError={onImgError} alt="" loading="lazy" className="h-16 w-20 flex-none rounded-lg object-cover" />
    <div className="min-w-0">
      <h4 className="text-sm font-bold leading-snug text-slate-900 line-clamp-2 group-hover:text-[#047857]">{item.title}</h4>
      <p className="mt-1 text-xs text-slate-400">{timeAgo(item.published_at)} · {item.source}</p>
    </div>
  </Link>
);

export const AthleteCard = ({ a, showSport = true }) => {
  const { t } = useT();
  return (
  <Link to={`/athletes/${a.slug}`} data-testid={`athlete-card-${a.slug}`} className="group card-hover block overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
    <div className="img-zoom relative aspect-[4/5] overflow-hidden bg-slate-100">
      {a.image ? <img src={a.image} alt={a.name} loading="lazy" className="h-full w-full object-cover object-top" /> :
        <div className="grid h-full w-full place-items-center bg-[#0B1120] text-center"><span className="font-display text-5xl font-black text-white/20">{a.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}</span></div>}
      <img src={FLAG} alt="Cameroon" className="absolute left-2 top-2 h-4 rounded-sm shadow" />
      {a.data_status === "missing" && <span className="absolute right-2 top-2 rounded bg-amber-500/90 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">{t("Data pending")}</span>}
    </div>
    <div className="flex items-center justify-between p-3">
      <div className="min-w-0">
        <h4 className="truncate text-[15px] font-bold text-slate-900">{a.name}</h4>
        <p className="truncate text-xs text-slate-500">{showSport ? t(sportLabel(a.sport)) : a.position}{a.club && showSport ? ` · ${a.club.split(" (")[0]}` : ""}</p>
      </div>
      <ChevronRight size={16} className="text-slate-400 transition-transform group-hover:translate-x-1" />
    </div>
  </Link>
  );
};

export const FixtureRow = ({ f, compact = false }) => {
  const finished = f.status === "finished";
  const live = f.status === "live";
  return (
    <div data-testid={`fixture-${f.id}`} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-3 text-sm">
      {!compact && <div className="hidden w-40 flex-none sm:block"><p className="truncate text-xs font-semibold text-slate-500">{f.competition}</p><p className="text-[11px] text-slate-400">{fmtDate(f.kickoff, false)}</p></div>}
      <div className="flex flex-1 items-center justify-end gap-2 text-right font-semibold text-slate-900"><span className="truncate">{f.home}</span>{f.home_badge && <img src={f.home_badge} alt="" className="h-5 w-5 object-contain" />}</div>
      <div className={`min-w-[64px] rounded-md px-2 py-1 text-center font-display text-lg font-bold ${live ? "bg-red-50 text-red-600" : finished ? "bg-gradient-to-br from-[#047857] to-[#0B1120] text-white" : "bg-[#F5B301]/15 text-[#8a6a00]"}`}>
        {finished || live ? `${f.home_score} - ${f.away_score}` : new Date(f.kickoff).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
      </div>
      <div className="flex flex-1 items-center gap-2 font-semibold text-slate-900">{f.away_badge && <img src={f.away_badge} alt="" className="h-5 w-5 object-contain" />}<span className="truncate">{f.away}</span></div>
      <div className="hidden w-16 flex-none text-right sm:block">
        {live ? <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-extrabold text-white">LIVE</span> : <span className="text-[11px] font-semibold uppercase text-slate-400">{finished ? "FT" : sportLabel(f.sport)}</span>}
      </div>
      {!finished && !live && <AddToCalendar f={f} compact />}
    </div>
  );
};

export const DarkHero = ({ title, subtitle, image, children, small = false }) => {
  const { t } = useT();
  return (
  <section className={`relative grain overflow-hidden bg-[#0B1120] text-white ${small ? "py-10" : "py-16 sm:py-20"}`} data-testid="page-hero">
    {image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />}
    <div className="absolute inset-0 bg-gradient-to-r from-[#0B1120] via-[#0B1120]/80 to-[#0B1120]/30" />
    <div className="container-x relative">
      <h1 className="font-display text-5xl font-black uppercase tracking-tight sm:text-6xl lg:text-7xl">{t(title)}</h1>
      {subtitle && <p className="mt-3 max-w-xl text-base text-slate-300 md:text-lg">{t(subtitle)}</p>}
      {children}
    </div>
  </section>
  );
};

export const Empty = ({ text = "No data available yet from online sources." }) => {
  const { t } = useT();
  return <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="empty-state">{t(text)}</div>;
};

export const Loader = () => (
  <div className="container-x py-20 text-center" data-testid="loader"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#047857]" /></div>
);
